import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { HasUnsavedChanges } from 'src/app/core/guards/unsaved-changes.guard';
import { AgentClientsService } from 'src/app/core/services/agent-clients.service';
import { QuestionnaireAnswerComponent } from 'src/app/shared/components/smart/questionnaire-answer/questionnaire-answer.component';
import { GuideService } from '../guide/services/guide.service';

@Component({
  selector: 'app-questionnaire-answer-client',
  templateUrl: './questionnaire-answer-client.component.html',
  styleUrls: ['./questionnaire-answer-client.component.scss'],
})
export class QuestionnaireAnswerClientComponent implements OnInit, OnDestroy, HasUnsavedChanges {
  @ViewChild(QuestionnaireAnswerComponent) questionnaireAnswerRef!: QuestionnaireAnswerComponent;

  claimId?: string;
  surveyId?: string;
  showQuestionnaire = true;

  private routeSub?: Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private agentClientsService: AgentClientsService,
    private guideService: GuideService
  ) {}

  ngOnInit(): void {
    // Angular's default RouteReuseStrategy reuses this component across claim/:cid/q/:qcode
    // navigations since the route config is the same. Reacting to paramMap (instead of reading
    // route.snapshot once in the constructor) and forcing the child to fully re-init on change
    // ensures fresh user/PII/questionnaire state is loaded per claim.
    this.routeSub = this.route.paramMap.subscribe((params) => {
      const cid = params.get('cid') ?? undefined;
      const sid = params.get('qcode') ?? undefined;

      if (cid === this.claimId && sid === this.surveyId) {
        return;
      }

      this.showQuestionnaire = false;
      this.claimId = cid;
      this.surveyId = sid;
      setTimeout(() => {
        this.showQuestionnaire = true;
      });
    });
  }

  ngOnDestroy(): void {
    this.routeSub?.unsubscribe();
  }

  hasUnsavedChanges(): boolean {
    return this.questionnaireAnswerRef?.hasUnsavedChanges() ?? false;
  }

  async onComplete(): Promise<void> {
    // CPK-4901: an agent keeps going through the claim's guide to the next pending
    // action. Only when the guide has something left — wizard off, inactive claim or
    // nothing pending would just bounce the guide to home, so those keep the claim page.
    if (this.claimId && this.agentClientsService.isAgentSession() && (await this.guideHasPendingActions(this.claimId))) {
      this.router.navigateByUrl(`/client/claim/${this.claimId}/guide`);
      return;
    }

    this.router.navigateByUrl('/client/claim/' + this.claimId);
  }

  private async guideHasPendingActions(claimId: string): Promise<boolean> {
    // The guide guard may have cached this claim's actions before the questionnaire
    // was answered; the guide reuses that cache, so reload it with the new answer.
    this.guideService.requirementsFormClaim = null;
    try {
      return await this.guideService.checkIfUserMustAccessGuide('all', claimId);
    } catch (error) {
      console.error('[QUESTIONNAIRE_ANSWER_CLIENT] Failed to check pending guide actions', error);
      return false;
    }
  }
}
